import React, { ReactNode } from "react";
import {
  FocusedComponentType,
  FocusedVia,
  PopupWindow,
  type PopupProps,
  type PreLoginContentAlignment,
} from "./types";
import { PopupCarousel } from "./PopupCarousel";
import { PopupTopBanner } from "./PopupMarquee";
import {
  SSOMarketingPopupProvider,
  useSSOMarketingPopupContext,
} from "./PopupProvider";
import { X } from "react-feather";
import { getHtmlBannerText, isMobilePreviewMode } from "./utils";
import { classNames } from "../helpers";

const previewSelectableRing = (isFocused: boolean) =>
  isFocused ? "ui-border-primary-dark ui-shadow-xl" : "ui-border-transparent";

const preLoginAlignUi = (align: PreLoginContentAlignment) => {
  switch (align) {
    case "left":
      return {
        headerJustify: "ui-justify-start",
        bodyOuter: "ui-justify-start",
        bodyInnerItems: "ui-items-start",
        textAlign: "ui-text-left",
      };
    case "right":
      return {
        headerJustify: "ui-justify-end",
        bodyOuter: "ui-justify-end",
        bodyInnerItems: "ui-items-end",
        textAlign: "ui-text-right",
      };
    default:
      return {
        headerJustify: "ui-justify-center",
        bodyOuter: "ui-justify-center",
        bodyInnerItems: "ui-items-center",
        textAlign: "ui-text-center",
      };
  }
};

export const Popup = ({ children, ...props }: PopupProps) => {
  return (
    <SSOMarketingPopupProvider {...props}>
      <PopupComponent>{children}</PopupComponent>
    </SSOMarketingPopupProvider>
  );
};

Popup.displayName = "Banner";

const PopupComponent = ({ children }: { children?: React.ReactNode }) => {
  const {
    popup: { backgroundColor, borderRadius, postLogin, card },
    currentWindow,
    hidePreLoginDecorations,
    overRides,
    showCloseButton = true,
    closeButtonPosition = "top-right",
  } = useSSOMarketingPopupContext();

  const shellBackground =
    currentWindow === PopupWindow.POST_LOGIN
      ? (postLogin?.backgroundColor ?? "")
      : backgroundColor;
  const shellBorderRadius = borderRadius ?? "16px";
  const isMobileBottomSheet = isMobilePreviewMode(overRides);

  const showTopCenterOutsideClose =
    showCloseButton &&
    closeButtonPosition === "top-center-outside" &&
    (!overRides?.isPreview || Boolean(overRides?.renderOutsideCloseInPreview));

  const renderCurrentWindow = () => {
    if (currentWindow === PopupWindow.POST_LOGIN && postLogin) {
      return (
        <div className="ui-flex ui-flex-1 ui-flex-col ui-min-h-0">
          <PopupHeader />
          <PopupPostLogin>{children}</PopupPostLogin>
        </div>
      );
    }

    return (
      <div className="ui-relative ui-overflow-hidden ui-flex ui-flex-1 ui-flex-col">
        <div className="ui-relative ui-z-10 ui-flex ui-flex-1 ui-flex-col">
          <PopupHeader />
          {!hidePreLoginDecorations && (
            <>
              <PopupBody />
              {card?.enabled === true && (
                <div className="ui-mt-4 md:ui-mt-5 ui-mb-1 md:ui-mb-0">
                  <PopupCarousel />
                </div>
              )}
            </>
          )}
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="ui-relative">
      {showTopCenterOutsideClose && (
        <div className="ui-absolute ui-left-1/2 ui-bottom-full -ui-translate-x-1/2 ui-mb-2 ui-z-20 ui-bg-transparent">
          <CloseButton />
        </div>
      )}
      <div
        style={{
          background: shellBackground,
          borderTopLeftRadius: shellBorderRadius,
          borderTopRightRadius: shellBorderRadius,
          borderBottomLeftRadius: isMobileBottomSheet ? 0 : shellBorderRadius,
          borderBottomRightRadius: isMobileBottomSheet ? 0 : shellBorderRadius,
          fontFamily: '"ShopfloPopupFont", "Inter", Arial, Helvetica, sans-serif',
          transition: "min-height 250ms ease",
        }}
        className={classNames(
          "ui-flex ui-flex-col ui-w-full ui-overflow-x-hidden ui-h-full ui-antialiased",
          currentWindow === PopupWindow.PRE_LOGIN && !hidePreLoginDecorations && card?.enabled === true
            ? overRides?.isPreview
              ? isMobilePreviewMode(overRides)
                ? "ui-min-h-[420px]"
                : "ui-min-h-[530px]"
              : isMobilePreviewMode(overRides)
                ? "ui-min-h-[420px]"
                : "ui-min-h-[530px] md:ui-min-h-[623px]"
            : "",
          overRides?.focusedComponent?.type === FocusedComponentType.SSO ? "ui-border ui-border-primary-dark" : "",
        )}
      >
        <PopupTopBanner />
        <div className="ui-flex ui-flex-1 ui-flex-col ui-w-full ui-pt-0">{renderCurrentWindow()}</div>
      </div>
    </div>
  );
};

const CloseButton = () => {
  const { onClose } = useSSOMarketingPopupContext();
  return (
    <button
      onClick={onClose}
      className="ui-flex ui-items-center ui-justify-center ui-rounded-full ui-bg-white ui-w-9 ui-h-9 ui-shadow-[0_2px_10px_rgba(0,0,0,0.18)]"
      aria-label="Close popup"
    >
      <X size={24} color="#4D4D4D" />
    </button>
  );
};

const PreviewModeDefaultLogo = () => {
  const { overRides, currentWindow } = useSSOMarketingPopupContext();
  const isLogoFocused =
    overRides?.focusedComponent?.type ===
    (currentWindow === PopupWindow.POST_LOGIN ? FocusedComponentType.POST_LOGIN_SSO : FocusedComponentType.BRAND);

  return (
    <button
      type="button"
      className={classNames(
        "ui-flex ui-flex-col w-fit gap-0.5 ui-bg-transparent ui-border ui-cursor-pointer ui-transition-colors",
        preLoginAlignUi("left").bodyInnerItems,
        previewSelectableRing(isLogoFocused),
      )}
      onClick={() => {
        overRides?.setFocusedComponent?.({
          type: currentWindow === PopupWindow.POST_LOGIN ? FocusedComponentType.POST_LOGIN_SSO : FocusedComponentType.BRAND,
          via: FocusedVia.POPUP,
        });
      }}
    >
      <span
        className={`ui-block ui-text-coal-lighter ui-font-bold ui-text-sm ui-px-1 ui-py-0.5 ${isMobilePreviewMode(overRides) ? "100px" : "150px"}`}
      >
        Logo
      </span>
    </button>
  );
};

const PopupMerchantLogo = () => {
  const {
    overRides,
    currentWindow,
    popup: { logoUrl },
  } = useSSOMarketingPopupContext();
  const isLogoFocused =
    overRides?.focusedComponent?.type ===
    (currentWindow === PopupWindow.POST_LOGIN ? FocusedComponentType.POST_LOGIN_SSO : FocusedComponentType.BRAND);

  if (!logoUrl && overRides?.isPreview) return <PreviewModeDefaultLogo />;

  if (!overRides?.isPreview) {
    return (
      <picture>
        <img
          src={logoUrl}
          alt="merchant logo"
          className="ui-h-[60px] ui-max-w-[140px] ui-w-auto ui-bg-transparent ui-object-contain"
        />
      </picture>
    );
  }

  return (
    <button
      type="button"
      className={classNames(
        "ui-flex ui-flex-col w-fit gap-0.5 ui-bg-transparent ui-border ui-cursor-pointer ui-transition-colors",
        preLoginAlignUi("left").bodyInnerItems,
        previewSelectableRing(isLogoFocused),
      )}
      onClick={() => {
        overRides?.setFocusedComponent?.({
          type:
            currentWindow === PopupWindow.POST_LOGIN
              ? FocusedComponentType.POST_LOGIN_SSO
              : FocusedComponentType.BRAND,
          via: FocusedVia.POPUP,
        });
      }}
    >
      <picture>
        <img
          src={logoUrl}
          alt="merchant logo"
          className="ui-h-[60px] ui-max-w-[140px] ui-bg-transparent ui-object-contain ui-px-1 ui-py-0.5"
        />
      </picture>
    </button>
  );
};

const PopupHeader = () => {
  const {
    overRides,
    closeButtonPosition = "top-right",
    showCloseButton = true,
    popup: { logoUrl, preLoginContentAlignment },
  } = useSSOMarketingPopupContext();

  // Post-login inherits pre-login alignment so logo/text positioning stays consistent across steps.
  const alignUi = preLoginAlignUi(preLoginContentAlignment ?? "center");

  const shouldRenderTopRightClose =
    showCloseButton && closeButtonPosition !== "top-center-outside" && !overRides?.isPreview;
  const hasHeaderContent = Boolean(logoUrl) || shouldRenderTopRightClose;

  return (
    <div
      className={classNames(
        "ui-relative ui-flex ui-items-center ui-w-full",
        alignUi.headerJustify,
        hasHeaderContent ? "ui-pt-6 ui-pb-4 ui-px-8" : "ui-pb-0",
      )}
    >
      {Boolean(logoUrl) && <PopupMerchantLogo />}
      {shouldRenderTopRightClose && (
        <span className="ui-absolute ui-right-0 ui-pr-2 ui-top-0">
          <CloseButton />
        </span>
      )}
    </div>
  );
};

const PopupBody = () => {
  const {
    popup: {
      title: { text: titleText, color: titleTextColor },
      subTitle,
      preLoginContentAlignment,
      card,
    },
    currentWindow,
    overRides,
  } = useSSOMarketingPopupContext();

  const align =
    currentWindow === PopupWindow.POST_LOGIN
      ? "center"
      : (preLoginContentAlignment ?? "center");
  const alignUi = preLoginAlignUi(align);

  const bodyPadding = card?.enabled === true ? "ui-pt-3 ui-pb-0" : "ui-pt-8 ui-pb-3";
  const isPreview = Boolean(overRides?.isPreview);

  return (
    <div
      className={classNames("ui-px-8 ui-flex", isPreview ? "ui-cursor-pointer" : "", bodyPadding, alignUi.bodyOuter)}
      onClick={
        isPreview
          ? () =>
              overRides?.setFocusedComponent?.({
                type: FocusedComponentType.TITLE_AND_SUBTITLE,
                via: FocusedVia.POPUP,
              })
          : undefined
      }
    >
      <div
        className={classNames(
          "ui-flex ui-flex-col w-fit gap-0.5",
          isPreview ? "ui-border ui-transition-colors" : "",
          alignUi.bodyInnerItems,
          isPreview
            ? previewSelectableRing(overRides?.focusedComponent?.type === FocusedComponentType.TITLE_AND_SUBTITLE)
            : "",
        )}
      >
        <p
          className={classNames("ui-text-text-xl-semibold", alignUi.textAlign)}
          style={{ color: titleTextColor }}
          dangerouslySetInnerHTML={{
            __html: getHtmlBannerText(titleText || "", overRides, "title"),
          }}
        />
        {subTitle && (
          <p
            className={classNames(
              "ui-leading-[22px]",
              alignUi.textAlign,
              isMobilePreviewMode(overRides) ? "ui-text-text-xs-medium" : "ui-text-text-sm-medium",
            )}
            style={{ color: subTitle.color }}
            dangerouslySetInnerHTML={{
              __html: getHtmlBannerText(subTitle.text || "", overRides, "subTitle"),
            }}
          />
        )}
      </div>
    </div>
  );
};

export const PopupPostLogin = ({ children }: { children: ReactNode }) => {
  const {
    popup: { postLogin, preLoginContentAlignment, card },
    overRides,
  } = useSSOMarketingPopupContext();

  if (!postLogin) return <></>;
  const { title, subtitle } = postLogin;
  // Post-login mirrors pre-login: alignment, padding, and typography stay consistent across steps.
  const alignUi = preLoginAlignUi(preLoginContentAlignment ?? "center");
  const bodyPadding = card?.enabled === true ? "ui-pt-3 ui-pb-0" : "ui-pt-8 ui-pb-3";
  const isPreview = Boolean(overRides?.isPreview);

  return (
    <div className="ui-flex ui-flex-1 ui-flex-col ui-min-h-0">
      <div
        className={classNames("ui-px-8 ui-flex", isPreview ? "ui-cursor-pointer" : "", bodyPadding, alignUi.bodyOuter)}
        onClick={
          isPreview
            ? () =>
                overRides?.setFocusedComponent?.({
                  type: FocusedComponentType.POST_LOGIN_TITLE_AND_SUBTITLE,
                  via: FocusedVia.POPUP,
                })
            : undefined
        }
      >
        <div
          className={classNames(
            "ui-flex ui-flex-col w-fit gap-0.5",
            isPreview ? "ui-border ui-transition-colors" : "",
            alignUi.bodyInnerItems,
            isPreview
              ? previewSelectableRing(
                  overRides?.focusedComponent?.type === FocusedComponentType.POST_LOGIN_TITLE_AND_SUBTITLE,
                )
              : "",
          )}
        >
          <p
            className={classNames("ui-text-text-xl-semibold", alignUi.textAlign)}
            style={{ color: title.color }}
            dangerouslySetInnerHTML={{
              __html: getHtmlBannerText(title.text || "", overRides, "title"),
            }}
          />
          <p
            className={classNames("ui-text-text-sm-medium ui-leading-[22px]", alignUi.textAlign)}
            style={{ color: subtitle.color }}
            dangerouslySetInnerHTML={{
              __html: getHtmlBannerText(subtitle.text || "", overRides, "subTitle"),
            }}
          />
        </div>
      </div>
      <div className="ui-flex ui-flex-1 ui-flex-col ui-justify-start ui-min-h-0">{children}</div>
    </div>
  );
};
